import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';
import {
  getUserSlackSettings,
  upsertUserSlackSettings as upsertUserSlackSettingsApi,
  deleteUserSlackSettings as deleteUserSlackSettingsApi,
} from '@kirin/api-client/api/account';
import {
  getOrganizationSlackSettings,
  upsertOrganizationSlackSettings as upsertOrganizationSlackSettingsApi,
  deleteOrganizationSlackSettings as deleteOrganizationSlackSettingsApi,
} from '@kirin/api-client/api/organizations';
import type { slackSettings } from '@kirin/api-client/contracts';

/**
 * Hook to fetch user's personal Slack settings
 */
export function useUserSlackSettings() {
  return useQuery({
    queryKey: queryKeys.slackSettings.user(),
    queryFn: () => getUserSlackSettings(apiClient),
    retry: retryHandlers.detail, // Don't retry on 404/auth errors
  });
}

/**
 * Hook to upsert user's personal Slack settings
 */
export function useUpsertUserSlackSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: slackSettings.UpsertSlackSettingsBody) =>
      upsertUserSlackSettingsApi(apiClient, data),
    onSuccess: (data: slackSettings.UpsertSlackSettingsResponse) => {
      // Update cache with new data (optimistic update on success)
      queryClient.setQueryData(
        queryKeys.slackSettings.user(),
        (old: slackSettings.SlackSettingsResponse | undefined) => ({
          ...old,
          ...data,
        })
      );
    },
  });
}

/**
 * Hook to delete user's personal Slack settings
 */
export function useDeleteUserSlackSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteUserSlackSettingsApi(apiClient),
    onSuccess: () => {
      // Remove settings from cache (set to null/undefined)
      queryClient.setQueryData(queryKeys.slackSettings.user(), null);
    },
  });
}

/**
 * Hook to fetch organization Slack settings
 */
export function useOrganizationSlackSettings(organizationId: string) {
  return useQuery({
    queryKey: queryKeys.slackSettings.organization(organizationId),
    queryFn: () => getOrganizationSlackSettings(apiClient, organizationId),
    enabled: !!organizationId, // Only run query if organizationId exists
    retry: retryHandlers.detail, // Don't retry on 404/auth errors
  });
}

/**
 * Hook to upsert organization Slack settings
 */
export function useUpsertOrganizationSlackSettings(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: slackSettings.UpsertSlackSettingsBody) =>
      upsertOrganizationSlackSettingsApi(apiClient, organizationId, data),
    onSuccess: (data: slackSettings.UpsertSlackSettingsResponse) => {
      // Update cache with new data (optimistic update on success)
      queryClient.setQueryData(
        queryKeys.slackSettings.organization(organizationId),
        (old: slackSettings.SlackSettingsResponse | undefined) => ({
          ...old,
          ...data,
        })
      );
    },
  });
}

/**
 * Hook to delete organization Slack settings
 */
export function useDeleteOrganizationSlackSettings(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      deleteOrganizationSlackSettingsApi(apiClient, organizationId),
    onSuccess: () => {
      // Remove settings from cache (set to null/undefined)
      queryClient.setQueryData(
        queryKeys.slackSettings.organization(organizationId),
        null
      );
    },
  });
}
